/* ─────────────────────────────────────────────────────────────────────────────
   Menü yolu (path) çözümlemesi — hem sidebar hem "Favorilerim" yıldızı kullanır.

   menuPathFallbacks: DB'deki bazı Menu satırlarında Path boş; adından yolu çözer.
   resolveMenuByPath: açık olan URL'yi menü ağacındaki bir yaprağa bağlar. Önce birebir
   eşleşme, yoksa EN UZUN önek (böylece /invoices/5 detay sayfası "Faturalar" menüsüne,
   /reports/order-list ise "/reports"e değil kendi menüsüne düşer).
   ──────────────────────────────────────────────────────────────────────────── */

export const menuPathFallbacks = {
  Overview: '/dashboard',
  Assistant: '/assistant',
  RuleCategories: '/rulecategories',
  WorkflowManagement: '/workflows',
  RoleManagement: '/role-management',
  MobilePermissions: '/mobile-permissions',
  UserManagement: '/user-management',
  TenantManagement: '/tenants',
  Tenants: '/tenants',
  TenantUsage: '/tenant-usage',
  Licenses: '/licenses',
  EtlPipelines: '/etl-pipelines',
  MyActions: '/actions',
  RuleManagement: '/rules',
  Sync: '/sync',
  Telemetry: '/telemetry',
  Announcements: '/announcements',
  Payment: '/credit-card-collection',
  PaymentTracking: '/payment',
  DBS: '/dbs',
  ProductDynamicGroup: '/product-dynamic-groups',
  ProductRestrictions: '/product-restrictions',
  ProductRouting: '/product-routing',
  CriticalSku: '/critical-skus',
  ProductAdditionalField: '/product-additional-fields',
  Products: '/products',
  ProductSegmentationTree: '/product-segmentation-tree',
  Prices: '/prices',
  CustomerAdditionalField: '/customer-additional-fields',
  CustomerDynamicGroup: '/customer-dynamic-groups',
  Customers: '/customers',
  Vendors: '/vendors',
  Warehouses: '/warehouses',
  ShipmentVehicles: '/shipment-vehicles',
  Distributors: '/distributors',
  Stocks: '/stocks',
  StockEntry: '/stock-entry',
  StockExit: '/stock-exit',
  StockTransfer: '/stock-transfer',
  VehicleTransfers: '/vehicle-transfers',
  TransitBalances: '/transit-balances',
  WarehouseStockReset: '/warehouse-stock-reset',
  Representatives: '/representatives',
  SalesOrgHierarchy: '/sales-org-hierarchy',
  Personnel: '/personnel',
  OrderHeaders: '/order-headers',
  PurchaseOrders: '/purchase-orders',
  ReturnOrders: '/return-orders',
  // #833-835 — iade türü başına ayrı ekran (üst başlıklar artık grup, yaprak bunlar).
  ReturnOrdersGood: '/return-orders-good',
  ReturnOrdersDamaged: '/return-orders-damaged',
  ReturnOrdersVendor: '/return-orders-vendor',
  ReturnWaybillsGood: '/return-waybills-good',
  ReturnWaybillsDamaged: '/return-waybills-damaged',
  ReturnWaybillsVendor: '/return-waybills-vendor',
  ReturnInvoicesGood: '/return-invoices-good',
  ReturnInvoicesDamaged: '/return-invoices-damaged',
  ReturnInvoicesVendor: '/return-invoices-vendor',
  Invoices: '/invoices',
  PurchaseInvoices: '/purchase-invoices',
  ReturnInvoices: '/return-invoices',
  VendorReturns: '/vendor-returns',
  GibMonitor: '/gib-monitor',
  EDocumentControl: '/e-belge-kontrol',
  DepositReturns: '/deposit-returns',
  DepositReconciliation: '/deposit-reconciliation',
  ReturnableQuantities: '/reports/returnable-quantities',
  SalesWaybills: '/waybills',
  PurchaseWaybills: '/purchase-waybills',
  ReturnWaybills: '/return-waybills',
  ServiceInvoicesGiven: '/service-invoices-given',
  ServiceInvoicesReceived: '/service-invoices-received',
  LinkRiskTracking: '/link-risk-tracking',
  AccountStatement: '/account-statement',
  ApprovalRequests: '/approval-requests',
  B2bReturns: '/b2b-returns',
  Visits: '/visits',
  VehicleLoads: '/vehicle-loads',
  VehicleCounts: '/vehicle-counts',
  DeliveryNotes: '/delivery-notes',
  Trainings: '/trainings',
  Campaigns: '/campaigns',
  CampaignAuthorizations: '/campaign-authorizations',
  Targets: '/targets',
  Agreements: '/agreements',
  Surveys: '/surveys',
  Coolers: '/coolers',
  CoolerFaults: '/cooler-faults',
  Assets: '/assets',
  AssetCounts: '/asset-counts',
  AssetCountOrders: '/asset-count-orders',
  AssetModels: '/asset-models',
  AssetAttributeDefs: '/asset-attribute-defs',
  AssetDrafts: '/asset-drafts',
  AssetImport: '/asset-import',
  ConsignmentWaybills: '/consignment-waybills',
  // #830 — Konsinye Alış İrsaliyesi (Type 8).
  ConsignmentPurchaseWaybills: '/consignment-purchase-waybills',
  CustomerTasks: '/customer-tasks',
  CustomerNotes: '/customer-notes',
  DepositCounts: '/deposit-counts',
  RoutePlans: '/route-plans',
  CustomerMemberships: '/customer-memberships',
  StandCounts: '/stand-counts',
  Reports: '/reports',
  ChiefDashboard: '/chief-dashboard',
  Notifications: '/notifications',
  SalesAnalytics: '/sales-analytics',
};

// Menü öğesinin (DB ya da hardcoded fallback) alan adları büyük/küçük harf karışık gelebilir.
export const menuId = (m) => m.id ?? m.Id ?? null;
export const menuName = (m) => m.name ?? m.Name ?? '';
export const menuPath = (m) => m.path ?? m.Path ?? menuPathFallbacks[menuName(m)] ?? '';

const normalize = (p) => {
  if (!p || p === '#') return '';
  const clean = p.split('?')[0].split('#')[0];
  return clean.length > 1 && clean.endsWith('/') ? clean.slice(0, -1) : clean;
};

/**
 * Açık olan URL'ye karşılık gelen menü öğesini döndürür (yoksa null).
 * Yalnız gerçek sayfası olan (path'i '#' olmayan) yapraklar aday.
 */
export function resolveMenuByPath(menus, pathname) {
  const current = normalize(pathname);
  if (!current || current === '/') return null;

  let best = null;
  let bestLen = 0;
  for (const m of menus || []) {
    const p = normalize(menuPath(m));
    if (!p) continue;
    if (p === current) return m;                                   // birebir → en iyi eşleşme
    // Önek eşleşmesi: /invoices/5 → /invoices (ama /invoices-x → /invoices DEĞİL)
    if (current.startsWith(p + '/') && p.length > bestLen) {
      best = m;
      bestLen = p.length;
    }
  }
  return best;
}
